/* ──────────────────────────────────────────────────────────────────────────
 * Counts, totals and rankings for the geospatial layer.
 *
 * Everything an operator might otherwise have to eyeball off the map: how
 * many ghats are mapped, how many police posts, how many people are in each
 * sector, which sector ranks worst and by how much.
 *
 * Kept as a pure service so the same numbers feed the layer panel, the
 * ranking table and any future export — rather than each surface counting
 * its own array and quietly disagreeing with the others.
 *
 * COUNTS ARE OF MAPPED OBJECTS, NOT OF THINGS THAT EXIST. "Hospitals: 2"
 * means two hospitals are in this dataset, not that Nashik has two. That
 * distinction is stated in the UI, because a count is the easiest number in
 * the system to misread as authoritative.
 * ────────────────────────────────────────────────────────────────────────── */

import {
  SECTORS, GHATS, ENTRY_GATES, EXIT_GATES, FACILITIES, ROUTES, CONGREGATION,
  sectorStatus,
} from "../data/kumbhGeo";
import { LAYERS } from "../config/layers";
import { INCIDENTS, SEVERITY, INCIDENT_TYPES } from "../data/operational";
import { TRUST } from "../data/provenance";

/* ── How many features each layer actually contains ────────────────────
 * Drives the count badges in the layer panel, so a toggle that would reveal
 * nothing is visibly empty before it is switched on. */
export function layerCounts() {
  const counts = {};
  LAYERS.forEach((l) => { counts[l.id] = 0; });

  counts.sectors = SECTORS.length;
  counts.ghats = GHATS.length;
  counts.congregation = CONGREGATION.length;
  counts.entryGates = ENTRY_GATES.length;
  counts.exitGates = EXIT_GATES.length;

  FACILITIES.forEach((f) => { counts[f.layer] = (counts[f.layer] || 0) + 1; });
  ROUTES.forEach((r) => { counts[r.layer] = (counts[r.layer] || 0) + 1; });

  return counts;
}

export function groupCount(groupKey, counts = layerCounts()) {
  return LAYERS.filter((l) => l.group === groupKey)
    .reduce((sum, l) => sum + (counts[l.id] || 0), 0);
}

/* ── Theatre-wide totals ───────────────────────────────────────────────── */
export function theatreTotals({ incidents = INCIDENTS } = {}) {
  const totalCurrent = SECTORS.reduce((s, x) => s + x.current, 0);
  const totalCapacity = SECTORS.reduce((s, x) => s + x.capacity, 0);
  const counts = layerCounts();
  const active = incidents.filter((i) => i.status !== "Resolved");

  const byStatus = { green: 0, amber: 0, red: 0 };
  SECTORS.forEach((s) => { byStatus[sectorStatus(s)] += 1; });

  return {
    pilgrims: totalCurrent,
    capacity: totalCapacity,
    headroom: totalCapacity - totalCurrent,
    occupancyPct: Math.round((totalCurrent / totalCapacity) * 100),
    sectors: SECTORS.length,
    sectorsGreen: byStatus.green,
    sectorsAmber: byStatus.amber,
    sectorsRed: byStatus.red,
    ghats: GHATS.length,
    entryGates: ENTRY_GATES.length,
    exitGates: EXIT_GATES.length,
    routesKm: null,           // not computed: see note in routeLengths()
    facilities: FACILITIES.length,
    mappedFeatures: Object.values(counts).reduce((a, b) => a + b, 0),
    incidentsTotal: incidents.length,
    incidentsActive: active.length,
    incidentsCritical: active.filter((i) => i.severity === "critical").length,
    trust: TRUST.SIMULATED,
  };
}

/* ── Ranked sector table ───────────────────────────────────────────────
 * The numbers behind the map, worst-first. `riskRows` is the output of
 * computeZoneRisk(); passing it in keeps the ranking consistent with the
 * risk panel instead of re-deriving a second, subtly different order. */
export function sectorTable({ riskRows = [], incidents = INCIDENTS } = {}) {
  const active = incidents.filter((i) => i.status !== "Resolved");
  const riskById = Object.fromEntries(riskRows.map((r) => [r.sectorId, r]));

  const rows = SECTORS.map((s) => {
    const risk = riskById[s.id];
    const mine = active.filter((i) => i.sector === s.id);
    return {
      id: s.id,
      name: s.name,
      centre: s.centre,
      current: s.current,
      capacity: s.capacity,
      headroom: s.capacity - s.current,
      occupancyPct: Math.round((s.current / s.capacity) * 100),
      status: sectorStatus(s),
      ghats: GHATS.filter((g) => g.sector === s.id).length,
      entryGates: ENTRY_GATES.filter((g) => g.sector === s.id).length,
      exitGates: EXIT_GATES.filter((g) => g.sector === s.id).length,
      facilities: FACILITIES.filter((f) => f.sector === s.id).length,
      incidents: mine.length,
      worstSeverity: mine.length
        ? mine.reduce((w, i) =>
            SEVERITY[i.severity].rank > SEVERITY[w].rank ? i.severity : w, "low")
        : null,
      riskScore: risk?.score ?? null,
      minutesToCritical: risk?.minutesToCritical ?? null,
      netPerMin: risk?.netPerMin ?? null,
      demandRatio: risk?.bottleneck?.demandRatio ?? null,
    };
  });

  // Rank by risk when available, otherwise by occupancy — and record the
  // rank on the row so the table and the map agree on ordering.
  rows.sort((a, b) =>
    (b.riskScore ?? b.occupancyPct) - (a.riskScore ?? a.occupancyPct)
  );
  rows.forEach((r, i) => { r.rank = i + 1; });
  return rows;
}

/* ── Incident breakdown ────────────────────────────────────────────────── */
export function incidentBreakdown({ incidents = INCIDENTS } = {}) {
  const active = incidents.filter((i) => i.status !== "Resolved");

  const byType = {};
  active.forEach((i) => { byType[i.type] = (byType[i.type] || 0) + 1; });

  const bySeverity = { critical: 0, high: 0, moderate: 0, low: 0 };
  active.forEach((i) => { bySeverity[i.severity] += 1; });

  return {
    total: incidents.length,
    active: active.length,
    resolved: incidents.length - active.length,
    bySeverity,
    byType: Object.entries(byType)
      .map(([type, count]) => ({
        type,
        count,
        label: INCIDENT_TYPES[type]?.label,
        color: INCIDENT_TYPES[type]?.color,
      }))
      .sort((a, b) => b.count - a.count),
  };
}

/* Route lengths are deliberately NOT computed into a headline "km of routes"
 * figure. The route geometry is schematic — straight lines between real
 * places, not snapped to the road network — so a total would be precise and
 * wrong, which is worse than absent. Compute it once routes come from a
 * department plan or are snapped to OSM ways. */
export function routeLengths() {
  return {
    available: false,
    reason:
      "Route geometry is schematic, not road-network routed. A kilometre " +
      "total would look authoritative while being wrong.",
    count: ROUTES.length,
  };
}
